import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigService } from 'src/app/service/config.service';

export interface DdSummaryInput {
  companyName: string;
  timeframeLabel: string;
  sentimentPct: number | null;
  volumeTotal: number | null;
  topEvents: { name: string; count: number }[];
}

// Same Summarizer endpoint the reporting service uses for DD tabular
// executive summaries. It lives on the LLM API base OUTSIDE the versioned
// chat paths (…/api/chat_completion, not …/api/v4/...). The auth interceptor
// attaches the customer token to urlChatLLMApi requests.
@Injectable({ providedIn: 'root' })
export class DdSummaryService {
  constructor(
    private http: HttpClient,
    private config: ConfigService,
  ) {}

  generateSummary(input: DdSummaryInput): Observable<string> {
    const url = `${this.config.appConfig.urlChatLLMApi}/chat_completion`;
    const body = {
      prompt: this.buildPrompt(input),
      // gpt-5-mini spends part of the budget on reasoning tokens; too small a
      // budget returns {"chat_completion": null}.
      max_tokens: 1500,
      temperature: 1,
      model_name: 'gpt-5-mini',
    };
    return this.http.post<Record<string, unknown>>(url, body).pipe(
      map((resp) => {
        const text = this.extractText(resp);
        if (!text) throw new Error('Empty summarizer response');
        return text;
      }),
    );
  }

  private buildPrompt(input: DdSummaryInput): string {
    const sentiment =
      input.sentimentPct !== null
        ? `${Math.round(input.sentimentPct)}%`
        : 'unavailable';
    const volume =
      input.volumeTotal !== null
        ? `${input.volumeTotal} articles`
        : 'unavailable';
    const events = input.topEvents.length
      ? input.topEvents
          .map((event) => `- ${event.name} (${event.count} mentions)`)
          .join('\n')
      : '- No specific event instances were detected.';

    return [
      `You are a senior credit analyst writing the executive summary of a news-based credit due diligence report on "${input.companyName}".`,
      '',
      `Observation window: ${input.timeframeLabel}.`,
      `Overall news sentiment: ${sentiment}.`,
      `News volume in the window: ${volume}.`,
      'Most frequent detected news events:',
      events,
      '',
      'Write exactly three short paragraphs, in this order:',
      '1. The company’s news profile and coverage volume over the window.',
      '2. What the sentiment level implies about market perception and momentum.',
      '3. The key event-driven credit risk factors and an overall risk takeaway.',
      '',
      'Constraints: plain text only — no markdown, no headers, no bullet points. Be factual and measured; do not invent events, figures or ratings beyond the data given. If data is marked unavailable, acknowledge the limited signal rather than speculating.',
    ].join('\n');
  }

  // Summarizer response shape (verified live): {"chat_completion": "<text>"}.
  // Anything else → '' → the caller throws → retry UI. Deliberately strict:
  // guessing at other fields risks rendering a status string as the summary.
  private extractText(resp: Record<string, unknown>): string {
    const text = resp?.['chat_completion'];
    return typeof text === 'string' ? text.trim() : '';
  }
}
